import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Select, Button, InputNumber, Icon, Upload, Input } from "antd";
import { Editor } from "@tinymce/tinymce-react";

import { Image } from "../../../services/api";
import { getImageLink } from "../../../services/asset";
import { notification } from "../../../services";
import { CategoryName, MakeModelInfoStatus } from "../../../models";
import { useMakes } from "../../../routes/Configuration/hooks";
import {
  fetchAdminMakeModelInfo,
  findAdminMakeModelInfoIdByMakeModel,
  publishAdminMakeModelInfo,
  saveAdminMakeModelInfo
} from "./makeModelInfo.ducks";
import {
  EngineSpec,
  MakeModelInfoSavePayload,
  SimilarModelPair,
  SimilarModelRef,
  SpecItem
} from "../../../typings/interfaces";

import styles from "./manageMakeModelInfoPage.scss";

interface MakeModelInfoFormModalProps {
  visible: boolean;
  editingId: string | null;
  editingStatus?: number | null;
  onCancel: () => void;
  onSaved: () => void;
}

interface MakeNode {
  id: string;
  name: string;
  route?: string;
  models?: ModelNode[];
}

interface ModelNode {
  id: string;
  name: string;
  route?: string;
}

interface FormState {
  id?: string;
  makeId?: string;
  modelId?: string;
  makeYearFrom?: number;
  makeYearTo?: number;
  title: string;
  imageName: string;
  overview: string;
  advantages: string[];
  drawbacks: string[];
  targetBuyer: string;
  engines: EngineSpec[];
  specifications: SpecItem[];
  buyingTips: string;
  safetyText: string;
  safetyEuroNcapUrl: string;
  similarModels: SimilarModelRef[];
  variants: SimilarModelPair[];
  status: number;
}

const emptyForm: FormState = {
  id: undefined,
  makeId: undefined,
  modelId: undefined,
  makeYearFrom: undefined,
  makeYearTo: undefined,
  title: "",
  imageName: "",
  overview: "",
  advantages: [],
  drawbacks: [],
  targetBuyer: "",
  engines: [],
  specifications: [],
  buyingTips: "",
  safetyText: "",
  safetyEuroNcapUrl: "",
  similarModels: [],
  variants: [],
  status: MakeModelInfoStatus.Draft
};

const emptyEngine: EngineSpec = { name: "", fuel: "", power: "", comment: "" };
const emptySpec: SpecItem = { label: "", value: "" };

interface ValidationErrors {
  [key: string]: string;
}
const emptyValidationErrors: ValidationErrors = {};

// data-* attribute used to locate the first inline field error in the DOM so
// we can scroll it into view after a failed submit. CSS-module classnames get
// hashed at build time, so a stable data attribute is easier to query than a
// styles.* lookup.
const FIELD_ERROR_ATTR = "data-mmi-field-error";

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "").trim();

// Read a message string off a fetch Response error body. Mirrors the pattern
// used in other admin flows (ManageUserPage createMember, ManageAdPage scrape)
// so BE-authored messages (e.g. variant conflicts naming the offending entry)
// surface to the admin instead of a generic toast.
const readBeErrorMessage = async (error: any) => {
  if (!error || typeof error.json !== "function") return null;
  try {
    const body = await error.json();
    if (body && typeof body.message === "string" && body.message) {
      return body.message;
    }
  } catch (e) {
    // Body wasn't JSON — fall through to the generic message.
  }
  return null;
};

const MakeModelInfoFormModal = (props: MakeModelInfoFormModalProps) => {
  const { visible, editingId, editingStatus, onCancel, onSaved } = props;
  const dispatch = useDispatch();
  // car makes with their models (configuration/makes?category=car)
  const carMakes = useMakes(CategoryName.car);

  const [form, setForm] = useState(emptyForm);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);

  // The modal body scrolls independently, and the primary action buttons sit
  // at the bottom — after a failed submit the first errored field is likely
  // off-screen. Scroll the first inline field error into view whenever errors
  // change so the user doesn't have to hunt for it.
  useEffect(() => {
    if (Object.keys(validationErrors).length === 0) return;
    const el: any = document.querySelector("[" + FIELD_ERROR_ATTR + "]");
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [validationErrors]);

  useEffect(() => {
    if (!visible) {
      setForm(emptyForm);
      return;
    }
    if (!editingId) {
      setForm(emptyForm);
      return;
    }
    let cancelled = false;
    setLoadingInitial(true);
    dispatch(fetchAdminMakeModelInfo(editingId, (data) => {
      if (cancelled) return;
      setLoadingInitial(false);
      if (!data) {
        notification.showError("Greška pri dohvatu info marke i modela");
        return;
      }
      const safety = data.safety || {};
      setForm({
        id: data.id,
        makeId: data.makeId,
        modelId: data.modelId,
        makeYearFrom: data.makeYearFrom,
        makeYearTo: data.makeYearTo,
        title: data.title || "",
        imageName: data.imageName || "",
        overview: data.overview || "",
        advantages: data.advantages || [],
        drawbacks: data.drawbacks || [],
        targetBuyer: data.targetBuyer || "",
        engines: data.engines || [],
        specifications: data.specifications || [],
        buyingTips: data.buyingTips || "",
        safetyText: safety.text || "",
        safetyEuroNcapUrl: safety.euroNcapUrl || "",
        similarModels: data.similarModels || [],
        variants: data.variants || [],
        status: data["status"] === undefined ? MakeModelInfoStatus.Draft : data["status"]
      });
    }));
    return () => {
      cancelled = true;
    };
  }, [visible, editingId]);

  // TinyMCE self-hosts via CDN so the app doesn't need to bundle its assets
  // (skins, plugins, icons) through webpack. The GPL build is free for
  // self-hosted use; no cloud API key is required.
  const tinymceInit = {
    height: 320,
    menubar: false,
    plugins: [
      "advlist", "autolink", "lists", "link", "table", "code", "help", "wordcount"
    ],
    toolbar:
      "undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor | " +
      "alignleft aligncenter alignright | bullist numlist | link | table | removeformat | code",
    // Wrap onto multiple rows instead of hiding overflow behind the "…" button.
    toolbar_mode: "wrap",
    // Full HR translation bundle; TinyMCE loads it from the CDN alongside core.
    language: "hr",
    language_url:
      "https://cdn.jsdelivr.net/npm/tinymce-i18n@25.1.28/langs6/hr.js",
    content_style:
      "body { font-family:Arial,Helvetica,sans-serif; font-size:14px; } " +
      "table { border-collapse: collapse; width: 100%; } " +
      "table td, table th { border: 1px solid #d9d9d9; padding: 6px 8px; }",
    branding: false,
    promotion: false,
    // Match the toolbar to the light background of the antd modal.
    skin: "oxide",
    content_css: "default"
  };

  const makes: MakeNode[] = carMakes || [];
  const selectedMake = makes.find(m => m.id === form.makeId);
  const models: ModelNode[] = (selectedMake && selectedMake.models) || [];

  const handleMakeChange = (makeId: string) => {
    setForm(prev => ({ ...prev, makeId, modelId: undefined }));
  };

  const handleModelChange = (modelId: string) => {
    setForm(prev => ({ ...prev, modelId }));
  };

  const handleImageUpload = (file: File) => {
    setUploading(true);
    Image.upload([file])
      .then(data => {
        const name = Array.isArray(data) ? data[0] : data;
        setForm(prev => ({ ...prev, imageName: name }));
        setUploading(false);
      })
      .catch(() => {
        notification.showError("Greška pri uploadu slike");
        setUploading(false);
      });
    return false;
  };

  const handleRemoveImage = () => {
    setForm(prev => ({ ...prev, imageName: "" }));
  };

  // Generic string-list handlers (advantages, drawbacks).
  const addStringItem = (field: string) => {
    setForm(prev => ({ ...prev, [field]: [...prev[field], ""] }));
  };
  const removeStringItem = (field: string, index: number) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };
  const updateStringItem = (field: string, index: number, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].map((v, i) => (i === index ? value : v))
    }));
  };

  // Engine repeater handlers.
  const handleEngineAdd = () => {
    setForm(prev => ({ ...prev, engines: [...prev.engines, { ...emptyEngine }] }));
  };
  const handleEngineRemove = (index: number) => {
    setForm(prev => ({
      ...prev,
      engines: prev.engines.filter((_, i) => i !== index)
    }));
  };
  const handleEngineChange = (index: number, key: string, value: string) => {
    setForm(prev => ({
      ...prev,
      engines: prev.engines.map((e, i) => (i === index ? { ...e, [key]: value } : e))
    }));
  };

  // Specification repeater handlers.
  const handleSpecAdd = () => {
    setForm(prev => ({
      ...prev,
      specifications: [...prev.specifications, { ...emptySpec }]
    }));
  };
  const handleSpecRemove = (index: number) => {
    setForm(prev => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index)
    }));
  };
  const handleSpecChange = (index: number, key: string, value: string) => {
    setForm(prev => ({
      ...prev,
      specifications: prev.specifications.map((s, i) =>
        i === index ? { ...s, [key]: value } : s
      )
    }));
  };

  const handleSimilarAdd = () => {
    setForm(prev => ({
      ...prev,
      similarModels: [
        ...prev.similarModels,
        { makeId: "", modelId: "" }
      ]
    }));
  };

  const handleSimilarRemove = (index: number) => {
    setForm(prev => ({
      ...prev,
      similarModels: prev.similarModels.filter((_, i) => i !== index)
    }));
  };

  const handleSimilarMakeChange = (index: number, makeId: string) => {
    setForm(prev => ({
      ...prev,
      similarModels: prev.similarModels.map((s, i) =>
        i === index ? { makeId, modelId: "" } : s
      )
    }));
  };

  const handleSimilarModelChange = (index: number, modelId: string) => {
    setForm(prev => ({
      ...prev,
      similarModels: prev.similarModels.map((s, i) =>
        i === index ? { ...s, modelId } : s
      )
    }));
  };

  const handleVariantAdd = () => {
    setForm(prev => ({
      ...prev,
      variants: [...prev.variants, { makeId: "", modelId: "" }]
    }));
  };

  const handleVariantRemove = (index: number) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleVariantMakeChange = (index: number, makeId: string) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { makeId, modelId: "" } : v
      )
    }));
  };

  const handleVariantModelChange = (index: number, modelId: string) => {
    setForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) =>
        i === index ? { ...v, modelId } : v
      )
    }));
  };

  const buildPayload = (): MakeModelInfoSavePayload | null => {
    // Collect per-field validation errors and render them inline under each
    // field, matching the ant Form "explain" style used elsewhere in the admin
    // (e.g. CreateAdminPopup, CreditsForm). The previous showError
    // notifications rendered behind the modal, so a user who missed a required
    // field had to close the modal (losing their input) to see what went wrong.
    const errors: ValidationErrors = {};
    if (!form.makeId) errors.makeId = "Marka je obavezna.";
    if (form.makeId && !form.modelId) errors.modelId = "Model je obavezan.";
    if (!form.makeYearFrom) errors.makeYearFrom = "Godina 'od' je obavezna.";
    if (!form.makeYearTo) errors.makeYearTo = "Godina 'do' je obavezna.";
    if (form.makeYearFrom && form.makeYearTo && form.makeYearFrom > form.makeYearTo) {
      errors.makeYearTo = "Godina 'do' mora biti veća ili jednaka godini 'od'.";
    }
    if (!form.imageName) errors.imageName = "Slika modela je obavezna.";
    // Overview replaces the old free-form "Sadržaj" as the primary section; the
    // page reads pretty empty without it, so require it. The rest is optional.
    if (!form.overview || stripHtml(form.overview).length === 0) {
      errors.overview = "Osnovne informacije o modelu su obavezne.";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return null;
    }
    setValidationErrors(emptyValidationErrors);

    const trimmedAdvantages = form.advantages.map(a => a.trim()).filter(Boolean);
    const trimmedDrawbacks = form.drawbacks.map(d => d.trim()).filter(Boolean);
    const cleanEngines = form.engines
      .map(e => ({
        name: (e.name || "").trim(),
        fuel: (e.fuel || "").trim(),
        power: (e.power || "").trim(),
        comment: (e.comment || "").trim()
      }))
      .filter(e => e.name || e.fuel || e.power || e.comment);
    const cleanSpecs = form.specifications
      .map(s => ({
        label: (s.label || "").trim(),
        value: (s.value || "").trim()
      }))
      .filter(s => s.label || s.value);

    const trimmedSafetyText = form.safetyText.trim();
    const trimmedSafetyUrl = form.safetyEuroNcapUrl.trim();
    const safety = (trimmedSafetyText || trimmedSafetyUrl)
      ? { text: trimmedSafetyText, euroNcapUrl: trimmedSafetyUrl || undefined }
      : undefined;

    const trimmedTitle = form.title.trim();
    return {
      id: form.id,
      makeId: form.makeId,
      modelId: form.modelId,
      makeYearFrom: form.makeYearFrom,
      makeYearTo: form.makeYearTo,
      imageName: form.imageName,
      title: trimmedTitle || undefined,
      overview: form.overview,
      advantages: trimmedAdvantages.length > 0 ? trimmedAdvantages : undefined,
      drawbacks: trimmedDrawbacks.length > 0 ? trimmedDrawbacks : undefined,
      targetBuyer: stripHtml(form.targetBuyer).length > 0 ? form.targetBuyer : undefined,
      engines: cleanEngines.length > 0 ? cleanEngines : undefined,
      specifications: cleanSpecs.length > 0 ? cleanSpecs : undefined,
      buyingTips: stripHtml(form.buyingTips).length > 0 ? form.buyingTips : undefined,
      safety,
      similarModels: form.similarModels
        .filter(s => s.makeId && s.modelId)
        .map(s => ({ makeId: s.makeId, modelId: s.modelId })),
      variants: form.variants
        .filter(v => v.makeId && v.modelId)
        .map(v => ({ makeId: v.makeId, modelId: v.modelId }))
    };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    dispatch(saveAdminMakeModelInfo(payload, async (result, error) => {
      setSaving(false);
      if (!result) {
        const beMessage = await readBeErrorMessage(error);
        notification.showError(beMessage || "Greška pri spremanju");
        return;
      }
      notification.show("Info marke i modela je spremljena");
      onSaved();
    }));
  };

  const finishPublish = (payload: MakeModelInfoSavePayload, savedId: string) => {
    dispatch(publishAdminMakeModelInfo(savedId, (ok) => {
      setPublishing(false);
      if (!ok) {
        // Save landed but publish endpoint reported an error. On edit the
        // content is safely persisted regardless of publish outcome, so treat
        // it as a plain save. On create the entry now exists as a draft.
        if (payload.id) {
          notification.show("Izmjene su spremljene");
        } else {
          notification.show("Spremljeno kao skica. Objavite iz liste.");
        }
        onSaved();
        return;
      }
      notification.show("Info marke i modela je objavljena");
      onSaved();
    }));
  };

  const handlePublish = () => {
    const payload = buildPayload();
    if (!payload) return;
    setPublishing(true);
    dispatch(saveAdminMakeModelInfo(payload, async (saved, error) => {
      if (!saved) {
        setPublishing(false);
        const beMessage = await readBeErrorMessage(error);
        notification.showError(beMessage || "Greška pri spremanju");
        return;
      }

      // Editing an entry that's already published shouldn't re-publish — the
      // status stays "Published" and the BE endpoint sometimes rejects the
      // redundant call. Just report the save and close.
      const wasAlreadyPublished =
        Boolean(payload.id) && editingStatus === MakeModelInfoStatus.Published;
      if (wasAlreadyPublished) {
        setPublishing(false);
        notification.show("Izmjene su spremljene");
        onSaved();
        return;
      }

      // BE may return the saved entity ({ id, ... }), just the id as a string,
      // or 204-no-content. Accept any shape, and fall back to the payload id
      // on edit since the FE already knows it then.
      let savedId = payload.id;
      if (typeof saved === "string") savedId = saved;
      else if (typeof saved === "object" && saved.id) savedId = saved.id;

      if (savedId) {
        finishPublish(payload, savedId);
        return;
      }

      // Create + BE didn't echo an id: look our just-saved entry up in the
      // list by (makeId, modelId) so we can still call publish without a page
      // reload.
      dispatch(findAdminMakeModelInfoIdByMakeModel(
        payload.makeId,
        payload.modelId,
        (foundId) => {
          if (!foundId) {
            setPublishing(false);
            // Save succeeded but we couldn't get an id — treat as draft-saved
            // so the modal still closes and the list refreshes. Admin can hit
            // publish from the row once BE returns an id.
            notification.show("Spremljeno kao skica. Objavite iz liste.");
            onSaved();
            return;
          }
          finishPublish(payload, foundId);
        }
      ));
    }));
  };

  // Inline error text mirroring antd v3's <Form.Item> "explain" — small red
  // line under the field. The data attribute lets the scroll effect target the
  // first errored field without depending on CSS-module hashes.
  const renderFieldError = (field: string) => {
    const message = validationErrors[field];
    if (!message) return null;
    return (
      <div className={styles.fieldError} {...{ [FIELD_ERROR_ATTR]: true }}>
        {message}
      </div>
    );
  };

  const renderSimilarRow = (similar: SimilarModelRef, index: number) => {
    const sMake = makes.find(m => m.id === similar.makeId);
    const sModels: ModelNode[] = (sMake && sMake.models) || [];
    return (
      <div key={index} className={styles.similarRow}>
        <Select
          showSearch
          placeholder="Marka"
          value={similar.makeId || undefined}
          onChange={(val: string) => handleSimilarMakeChange(index, val)}
          optionFilterProp="children"
          className={styles.similarSelect}
          getPopupContainer={trigger => trigger.parentElement}
        >
          {makes.map(m => (
            <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
          ))}
        </Select>
        <Select
          showSearch
          placeholder="Model"
          value={similar.modelId || undefined}
          onChange={(val: string) => handleSimilarModelChange(index, val)}
          optionFilterProp="children"
          className={styles.similarSelect}
          disabled={!similar.makeId}
          getPopupContainer={trigger => trigger.parentElement}
        >
          {sModels.map(m => (
            <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
          ))}
        </Select>
        <Button
          onClick={() => handleSimilarRemove(index)}
          className={styles.similarRemove}
        >
          <Icon type="delete" />
        </Button>
      </div>
    );
  };

  const renderVariantRow = (variant: SimilarModelPair, index: number) => {
    const vMake = makes.find(m => m.id === variant.makeId);
    const vModels: ModelNode[] = (vMake && vMake.models) || [];
    return (
      <div key={index} className={styles.similarRow}>
        <Select
          showSearch
          placeholder="Marka"
          value={variant.makeId || undefined}
          onChange={(val: string) => handleVariantMakeChange(index, val)}
          optionFilterProp="children"
          className={styles.similarSelect}
          getPopupContainer={trigger => trigger.parentElement}
        >
          {makes.map(m => (
            <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
          ))}
        </Select>
        <Select
          showSearch
          placeholder="Model"
          value={variant.modelId || undefined}
          onChange={(val: string) => handleVariantModelChange(index, val)}
          optionFilterProp="children"
          className={styles.similarSelect}
          disabled={!variant.makeId}
          getPopupContainer={trigger => trigger.parentElement}
        >
          {vModels.map(m => (
            <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
          ))}
        </Select>
        <Button
          onClick={() => handleVariantRemove(index)}
          className={styles.similarRemove}
        >
          <Icon type="delete" />
        </Button>
      </div>
    );
  };

  const renderStringList = (
    field: string,
    placeholder: string,
    addLabel: string
  ) => (
    <div>
      <div className={styles.similarList}>
        {form[field].map((val, i) => (
          <div key={i} className={styles.similarRow}>
            <Input
              value={val}
              placeholder={placeholder}
              onChange={e => updateStringItem(field, i, e.target.value)}
              className={styles.similarSelect}
            />
            <Button
              onClick={() => removeStringItem(field, i)}
              className={styles.similarRemove}
            >
              <Icon type="delete" />
            </Button>
          </div>
        ))}
      </div>
      <Button onClick={() => addStringItem(field)} className={styles.similarAdd}>
        <Icon type="plus" /> {addLabel}
      </Button>
    </div>
  );

  const renderEngineRow = (engine: EngineSpec, index: number) => (
    <div key={index} className={styles.engineRow}>
      <Input
        value={engine.name}
        placeholder="Motor (npr. 1.4 TSI)"
        onChange={e => handleEngineChange(index, "name", e.target.value)}
      />
      <Input
        value={engine.fuel}
        placeholder="Gorivo (npr. Benzin)"
        onChange={e => handleEngineChange(index, "fuel", e.target.value)}
      />
      <Input
        value={engine.power}
        placeholder="Snaga (npr. 85–110 KS)"
        onChange={e => handleEngineChange(index, "power", e.target.value)}
      />
      <Input
        value={engine.comment}
        placeholder="Komentar"
        onChange={e => handleEngineChange(index, "comment", e.target.value)}
      />
      <Button
        onClick={() => handleEngineRemove(index)}
        className={styles.similarRemove}
      >
        <Icon type="delete" />
      </Button>
    </div>
  );

  const renderSpecRow = (spec: SpecItem, index: number) => (
    <div key={index} className={styles.similarRow}>
      <Input
        value={spec.label}
        placeholder="Naziv (npr. Klasa)"
        onChange={e => handleSpecChange(index, "label", e.target.value)}
        className={styles.similarSelect}
      />
      <Input
        value={spec.value}
        placeholder="Vrijednost (npr. Kompaktni automobil)"
        onChange={e => handleSpecChange(index, "value", e.target.value)}
        className={styles.similarSelect}
      />
      <Button
        onClick={() => handleSpecRemove(index)}
        className={styles.similarRemove}
      >
        <Icon type="delete" />
      </Button>
    </div>
  );

  // Editing an already-published entry: the primary action is "Spremi" (which
  // handlePublish resolves as save-only), and the "Spremi kao skicu" button is
  // hidden — there's no reason to demote a published article to draft from
  // this dialog. Status comes from the list row, not from getById (which
  // doesn't include the status field).
  const isPublishedEdit =
    Boolean(editingId) && editingStatus === MakeModelInfoStatus.Published;

  return (
    <Modal
      title={editingId ? "Uredi info marke i modela" : "Nova info marke i modela"}
      visible={visible}
      onCancel={onCancel}
      width={1000}
      destroyOnClose
      footer={
        <div className={styles.modalFooter}>
          <Button onClick={onCancel}>Odustani</Button>
          {!isPublishedEdit && (
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={publishing}
            >
              Spremi kao skicu
            </Button>
          )}
          <Button
            type="primary"
            onClick={handlePublish}
            loading={publishing}
            disabled={saving}
          >
            {isPublishedEdit ? "Spremi" : "Spremi i objavi"}
          </Button>
        </div>
      }
    >
      {loadingInitial ? (
        <div className={styles.formLoading}>Učitavanje…</div>
      ) : (
        <div className={styles.formGrid}>
          <div className={styles.formRow}>
            <div className={styles.formCol}>
              <label className={styles.label}>Marka *</label>
              <Select
                showSearch
                placeholder="Odaberi marku"
                value={form.makeId}
                onChange={handleMakeChange}
                optionFilterProp="children"
                className={styles.fullWidth}
                getPopupContainer={trigger => trigger.parentElement}
              >
                {makes.map(m => (
                  <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
                ))}
              </Select>
              {renderFieldError("makeId")}
            </div>
            <div className={styles.formCol}>
              <label className={styles.label}>Model *</label>
              <Select
                showSearch
                placeholder="Odaberi model"
                value={form.modelId}
                onChange={handleModelChange}
                optionFilterProp="children"
                className={styles.fullWidth}
                disabled={!form.makeId}
                getPopupContainer={trigger => trigger.parentElement}
              >
                {models.map(m => (
                  <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>
                ))}
              </Select>
              {renderFieldError("modelId")}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Varijante</label>
            <div className={styles.sectionHelp}>
              Dodatne (marka, model) kombinacije koje pokriva ova info stranica —
              npr. Golf VII + Golf VII Variant. Glavna marka i model iznad ostaju
              primarni (SEO, URL, EuroNCAP). Varijante proširuju samo dohvat
              oglasa koji se prikazuju i CTA "Više informacija o modelu".
            </div>
            <div className={styles.similarList}>
              {form.variants.map(renderVariantRow)}
            </div>
            <Button onClick={handleVariantAdd} className={styles.similarAdd}>
              <Icon type="plus" /> Dodaj varijantu
            </Button>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formCol}>
              <label className={styles.label}>Godina od *</label>
              <InputNumber
                value={form.makeYearFrom}
                onChange={val => setForm(prev => ({ ...prev, makeYearFrom: val }))}
                min={1900}
                max={2100}
                className={styles.fullWidth}
              />
              {renderFieldError("makeYearFrom")}
            </div>
            <div className={styles.formCol}>
              <label className={styles.label}>Godina do *</label>
              <InputNumber
                value={form.makeYearTo}
                onChange={val => setForm(prev => ({ ...prev, makeYearTo: val }))}
                min={1900}
                max={2100}
                className={styles.fullWidth}
              />
              {renderFieldError("makeYearTo")}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Naslov stranice (opcionalno)</label>
            <Input
              value={form.title}
              placeholder="Ostavi prazno za auto-generirani naslov (npr. Volkswagen Golf VII (2012–2019))"
              onChange={e => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, title: value }));
              }}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Slika modela *</label>
            {form.imageName ? (
              <div className={styles.imagePreview}>
                <img src={getImageLink(form.imageName)} alt="Pregled" />
                <Button onClick={handleRemoveImage} className={styles.imageRemove}>
                  <Icon type="delete" /> Ukloni sliku
                </Button>
              </div>
            ) : (
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageUpload}
                disabled={uploading}
              >
                <Button loading={uploading}>
                  <Icon type="upload" /> {uploading ? "Upload…" : "Učitaj sliku"}
                </Button>
              </Upload>
            )}
            {renderFieldError("imageName")}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Osnovne informacije o modelu *</label>
            <div className={styles.editorWrapper}>
              <Editor
                tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@5.10.9/tinymce.min.js"
                value={form.overview}
                onEditorChange={val => setForm(prev => ({ ...prev, overview: val }))}
                init={tinymceInit}
              />
            </div>
            {renderFieldError("overview")}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Prednosti modela</label>
            {renderStringList("advantages", "Npr. Velika ponuda rabljenih primjeraka", "Dodaj prednost")}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Mogući nedostaci</label>
            {renderStringList("drawbacks", "Npr. Cijene rabljenih primjeraka su više", "Dodaj nedostatak")}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Za koga je ovaj model dobar izbor?</label>
            <div className={styles.editorWrapper}>
              <Editor
                tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@5.10.9/tinymce.min.js"
                value={form.targetBuyer}
                onEditorChange={val => setForm(prev => ({ ...prev, targetBuyer: val }))}
                init={tinymceInit}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Najčešći motori u ponudi</label>
            <div className={styles.engineList}>
              {form.engines.map(renderEngineRow)}
            </div>
            <Button onClick={handleEngineAdd} className={styles.similarAdd}>
              <Icon type="plus" /> Dodaj motor
            </Button>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Osnovne karakteristike</label>
            <div className={styles.similarList}>
              {form.specifications.map(renderSpecRow)}
            </div>
            <Button onClick={handleSpecAdd} className={styles.similarAdd}>
              <Icon type="plus" /> Dodaj karakteristiku
            </Button>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Na što paziti pri kupnji?</label>
            <div className={styles.editorWrapper}>
              <Editor
                tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@5.10.9/tinymce.min.js"
                value={form.buyingTips}
                onEditorChange={val => setForm(prev => ({ ...prev, buyingTips: val }))}
                init={tinymceInit}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Sigurnost — opis (opcionalno)</label>
            <Input.TextArea
              rows={3}
              value={form.safetyText}
              placeholder="Napomena uz Euro NCAP test (npr. na koju se verziju i godinu odnosi)."
              onChange={e => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, safetyText: value }));
              }}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Sigurnost — Euro NCAP URL (opcionalno)</label>
            <Input
              value={form.safetyEuroNcapUrl}
              placeholder="https://www.euroncap.com/…"
              onChange={e => {
                const value = e.target.value;
                setForm(prev => ({ ...prev, safetyEuroNcapUrl: value }));
              }}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Slični modeli</label>
            <div className={styles.similarList}>
              {form.similarModels.map(renderSimilarRow)}
            </div>
            <Button onClick={handleSimilarAdd} className={styles.similarAdd}>
              <Icon type="plus" /> Dodaj sličan model
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MakeModelInfoFormModal;



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageMakeModelInfoPage/MakeModelInfoFormModal.tsx